import React, { useMemo, useRef, useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  BellIcon,
  AdjustmentsHorizontalIcon,
  ChevronDownIcon,
  FunnelIcon,
  CalendarIcon,
  TruckIcon,
  DocumentTextIcon,
  ReceiptPercentIcon,
  MapPinIcon,
  ClipboardDocumentListIcon,
  CircleStackIcon,
  PencilIcon,
  TrashIcon
} from '@heroicons/react/24/outline';

// Notification data and helpers from DriverHistory
const notificationsData = [
  {
    id: 1,
    type: 'rca',
    title: 'RCA expiră în curând',
    description: 'RCA pentru Toyota Corolla (CJ-22-NTG) expiră în 14 zile',
    date: '2023-12-15',
    status: 'pending',
    carName: 'Toyota Corolla',
    carPlate: 'CJ-22-NTG',
    isRead: false
  },
  {
    id: 2,
    type: 'casco',
    title: 'CASCO va expira',
    description: 'CASCO pentru BMW X5 (B-123-BMW) expiră în 30 zile',
    date: '2023-12-30',
    status: 'pending',
    carId: 'CAR54321',
    carName: 'BMW X5',
    carPlate: 'B-123-BMW',
    isRead: true
  },
  {
    id: 3,
    type: 'appointment',
    title: 'Programare service confirmată',
    description: 'Programare la Service Auto Pro pentru schimb de ulei',
    date: '2023-11-25',
    status: 'confirmed',
    carId: 'CAR12345',
    carName: 'Toyota Corolla',
    carPlate: 'CJ-22-NTG',
    isRead: true,
    serviceName: 'Service Auto Pro'
  },
  {
    id: 4,
    type: 'rovinieta',
    title: 'Rovinieta expirată',
    description: 'Rovinieta pentru Ford Focus (CJ-01-CXA) a expirat',
    date: '2023-11-10',
    status: 'expired',
    carId: 'CAR98765',
    carName: 'Ford Focus',
    carPlate: 'CJ-01-CXA',
    isRead: false
  },
  {
    id: 5,
    type: 'itp',
    title: 'ITP în curând',
    description: 'ITP pentru Honda Civic (BV-01-BTN) este peste 7 zile',
    date: '2023-11-20',
    status: 'pending',
    carId: 'CAR24680',
    carName: 'Honda Civic',
    carPlate: 'BV-01-BTN',
    isRead: true
  }
];

// Helper functions from DriverHistory
const getTypeIcon = (type) => {
  const iconProps = "h-5 w-5 text-white";
  switch (type) {
    case 'rca': return <DocumentTextIcon className={iconProps} />;
    case 'casco': return <ReceiptPercentIcon className={iconProps} />;
    case 'appointment': return <CalendarIcon className={iconProps} />;
    case 'rovinieta': return <MapPinIcon className={iconProps} />;
    case 'itp': return <ClipboardDocumentListIcon className={iconProps} />;
    default: return <CircleStackIcon className={iconProps} />;
  }
};
const getStatusIndicator = (status) => {
  switch (status) {
    case 'pending': return 'bg-amber-500';
    case 'confirmed': return 'bg-blue-500';
    case 'expired': return 'bg-rose-500';
    case 'completed': return 'bg-emerald-500';
    case 'cancelled': return 'bg-slate-500';
    default: return 'bg-gray-400';
  }
};
const getStatusText = (status, t) => {
  const key = `notificationsHistory.status.${status}`;
  const translation = t(key);
  return translation === key ? status : translation;
};

const filterOptions = ['all', 'rca', 'casco', 'appointment', 'rovinieta', 'itp'];

const DriverNotificationsHistory = () => {
  const { t } = useTranslation();
  const [activeFilter, setActiveFilter] = useState('all');
  const [dateSort, setDateSort] = useState('desc');
  const [isFilterDropdownOpen, setIsFilterDropdownOpen] = useState(false);
  const [notifications, setNotifications] = useState(notificationsData);
  const filterDropdownRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (filterDropdownRef.current && !filterDropdownRef.current.contains(event.target)) {
        setIsFilterDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [filterDropdownRef]);

  // Handler for delete
  const handleDelete = (id) => {
    setNotifications((prev) => prev.filter((n) => n.id !== id));
  };
  // Handler for edit (placeholder)
  const handleEdit = (notification) => {
    // TODO: open modal or inline edit
    alert(`Editează notificarea: ${notification.title}`);
  };

  const filteredNotifications = useMemo(() => {
    return notifications.filter(notification => {
      if (activeFilter === 'all') return true;
      return notification.type === activeFilter;
    }).sort((a, b) => {
      if (dateSort === 'desc') {
        return new Date(b.date) - new Date(a.date);
      } else {
        return new Date(a.date) - new Date(b.date);
      }
    });
  }, [notifications, activeFilter, dateSort]);

  const getTypeName = (type) => {
    return t(`notificationsHistory.filterTypes.${type}`);
  };

  const formatDate = (dateString) => {
    const options = { year: 'numeric', month: 'short', day: 'numeric' };
    return new Date(dateString).toLocaleDateString('ro-RO', options);
  };

  return (
    <div className="space-y-6">
      <div className="bg-slate-100 dark:bg-slate-800 rounded-xl shadow-sm border border-slate-200 dark:border-slate-700">
        {/* Header */}
        <div className="px-2 py-2 border-b border-slate-200 dark:border-slate-700 bg-purple-50 dark:bg-purple-900/20 rounded-t-xl">
          <div className="flex items-center space-x-3">
            <div className="p-2 bg-purple-100 dark:bg-purple-900/30 rounded-lg">
              <BellIcon className="w-5 h-5 text-purple-600 dark:text-purple-400" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-slate-900 dark:text-white">
                {t('notificationsHistory.title')}
              </h3>
              <p className="text-sm text-slate-600 dark:text-slate-400">
                {t('notificationsHistory.subtitle')}
              </p>
            </div>
          </div>
        </div>

        {/* Filter and Sort Bar */}
        <div className="px-2 py-4 border-b border-slate-200 dark:border-slate-700">
          <div className="flex items-center justify-between gap-3">
            <div ref={filterDropdownRef} className="relative inline-block text-left">
              <button
                type="button"
                onClick={() => setIsFilterDropdownOpen(!isFilterDropdownOpen)}
                className="inline-flex w-full justify-center items-center gap-x-1.5 rounded-md bg-slate-100 dark:bg-slate-700 px-3 py-2 text-xs font-medium text-slate-700 dark:text-slate-300 shadow-sm hover:bg-gray-200 dark:hover:bg-gray-600"
                aria-expanded={isFilterDropdownOpen}
                aria-haspopup="true"
              >
                <AdjustmentsHorizontalIcon className="h-4 w-4 mr-1 text-slate-500 dark:text-slate-400" aria-hidden="true" />
                {t('notificationsHistory.filter')}: {getTypeName(activeFilter)}
                <ChevronDownIcon className={`-mr-1 h-4 w-4 text-slate-500 dark:text-slate-400 ${isFilterDropdownOpen ? 'rotate-180' : ''}`} aria-hidden="true" />
              </button>
              {isFilterDropdownOpen && (
                <div className="absolute left-0 z-10 mt-2 w-36 origin-top-right rounded-md bg-white dark:bg-slate-800 shadow-lg ring-1 ring-black ring-opacity-5 dark:ring-gray-700 focus:outline-none">
                  <div className="py-1" role="none">
                    {filterOptions.map((filterType) => (
                      <button
                        key={filterType}
                        onClick={() => { setActiveFilter(filterType); setIsFilterDropdownOpen(false); }}
                        className={`group flex w-full items-center px-4 py-2 text-sm ${activeFilter === filterType ? 'bg-purple-100 dark:bg-purple-900/50 text-purple-700 dark:text-purple-300' : 'text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-gray-700'}`}
                        role="menuitem"
                        tabIndex="-1"
                      >
                        {getTypeName(filterType)}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
            <button
              onClick={() => setDateSort(dateSort === 'desc' ? 'asc' : 'desc')}
              className="flex items-center px-3 py-2 bg-slate-100 dark:bg-slate-700 rounded-md text-xs text-slate-700 dark:text-slate-300 hover:bg-gray-200 dark:hover:bg-gray-600"
            >
              <FunnelIcon className="h-4 w-4 mr-1 text-slate-500 dark:text-slate-400" />
              <span>{t(`notificationsHistory.sortOptions.${dateSort}`)}</span>
            </button>
          </div>
        </div>

        {/* Notifications List */}
        <div className="px-2 py-4">
          <div className="space-y-3">
            {filteredNotifications.length > 0 ? (
              filteredNotifications.map(notification => (
                <div
                  key={notification.id}
                  className={`p-3 rounded-lg flex items-start gap-3  ${notification.isRead ? 'bg-white dark:bg-slate-800 hover:bg-slate-50 dark:hover:bg-gray-700/50' : 'bg-yellow-50 dark:bg-yellow-900/20 hover:bg-yellow-100 dark:hover:bg-yellow-900/30 border border-yellow-200 dark:border-yellow-800'}`}
                >
                  {/* Icon */}
                  <div className="flex-shrink-0 w-10 h-10 rounded-full bg-purple-700 flex items-center justify-center mt-0.5">
                    {getTypeIcon(notification.type)}
                  </div>
                  {/* Content */}
                  <div className="flex-1">
                    <div className="flex justify-between items-start mb-1">
                      <h4 className="text-sm font-semibold text-slate-900 dark:text-white">
                        {notification.title}
                      </h4>
                      {/* Status Dot */}
                      <div className="flex items-center text-xs text-slate-500 dark:text-slate-400 mt-0.5">
                        <span className={`w-2 h-2 rounded-full mr-1.5 ${getStatusIndicator(notification.status)}`}></span>
                        {getStatusText(notification.status, t)}
                      </div>
                    </div>
                    <p className="text-sm text-slate-600 dark:text-slate-300">
                      {notification.description}
                    </p>
                    {/* Meta Info */}
                    <div className="mt-2 flex flex-wrap items-center text-xs text-slate-500 dark:text-slate-400 gap-x-3 gap-y-1">
                      <span className="flex items-center">
                        <TruckIcon className="h-3.5 w-3.5 mr-1 text-slate-400" />
                        {notification.carName} ({notification.carPlate})
                      </span>
                      <span className="flex items-center">
                        <CalendarIcon className="h-3.5 w-3.5 mr-1 text-slate-400" />
                        {formatDate(notification.date)}
                      </span>
                      {!notification.isRead && (
                        <span className="text-amber-600 dark:text-yellow-400 font-medium">● {t('notificationsHistory.markAsUnread')}</span>
                      )}
                    </div>
                  </div>
                  {/* Edit/Delete Buttons */}
                  <div className="flex flex-col gap-2 ml-2">
                    <button
                      onClick={() => handleEdit(notification)}
                      className="p-1.5 rounded-md bg-slate-100 dark:bg-slate-700 hover:bg-purple-100 dark:hover:bg-purple-900/40 text-slate-600 dark:text-slate-300"
                      title={t('notificationsHistory.markAsRead')}
                    >
                      <PencilIcon className="h-5 w-5" />
                    </button>
                    <button
                      onClick={() => handleDelete(notification.id)}
                      className="p-1.5 rounded-md bg-slate-100 dark:bg-slate-700 hover:bg-red-100 dark:hover:bg-red-900/40 text-rose-600 dark:text-red-400"
                      title={t('notificationsHistory.deleteNotification')}
                    >
                      <TrashIcon className="h-5 w-5" />
                    </button>
                  </div>
                </div>
              ))
            ) : (
              <div className="text-center py-8 text-slate-500 dark:text-slate-400">
                <BellIcon className="h-10 w-10 mx-auto text-slate-400 dark:text-slate-500 mb-2" />
                {t('notificationsHistory.noNotificationsDesc')}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default DriverNotificationsHistory;
